import React from "react";
import { Link } from "react-router-dom";
import PublicNav from "@/components/PublicNav";
import Footer from "@/components/Footer";

function Section({ eyebrow, title, children, testid }) {
  return (
    <section className="max-w-4xl mx-auto px-6 md:px-10 py-14 border-t border-line" data-testid={testid}>
      <div className="overline">{eyebrow}</div>
      <h2 className="h-serif text-3xl md:text-4xl text-ink mt-3 leading-tight">{title}</h2>
      <div className="mt-6 text-sub leading-relaxed space-y-4 text-[15px]">{children}</div>
    </section>
  );
}

function Bullet({ children, testid }) {
  return (
    <li className="flex gap-3 items-start" data-testid={testid}>
      <span className="font-mono text-oxblood text-xs mt-1.5 shrink-0">▸</span>
      <span>{children}</span>
    </li>
  );
}

export default function Trust() {
  const lastReviewed = "February 2026";
  const complianceEmail = "campbellscommoditiesbrokerage@gmail.com";

  return (
    <div className="bg-parchment min-h-screen" data-testid="trust-page">
      <PublicNav />

      <section className="max-w-4xl mx-auto px-6 md:px-10 pt-24 pb-14">
        <div className="overline">Trust &amp; Compliance</div>
        <h1 className="h-serif text-5xl md:text-6xl text-ink mt-4 leading-[1.05]">
          The compliance gate — published openly.
        </h1>
        <p className="text-sub mt-6 leading-relaxed max-w-2xl">
          Every counterparty on our platform — buyer and supplier — clears the
          same corporate KYC and sanctions screening before any commercial
          conversation begins. This page describes exactly what that gate looks
          like, so your in-house counsel or compliance officer can evaluate us
          from a single URL.
        </p>
      </section>

      <Section eyebrow="01 · Who we are" title="Pure brokerage. No inventory. No client funds." testid="trust-who">
        <p>
          Campbell&apos;s Commodities Brokerage LLC is a U.S. limited-liability
          company operating as a pure introducing broker in the physical
          commodities market — sugar, coffee, crude and refined petroleum.
        </p>
        <p>
          We do not hold, warehouse, take title to, or take custody of any
          product. We do not custody client funds. Every transaction settles
          bank-to-bank; our brokerage commission is cut off the top by IMFPA
          lodged at the transaction banks.
        </p>
      </Section>

      <Section eyebrow="02 · Sanctions screening" title="Every counterparty is screened against consolidated global lists — live." testid="trust-sanctions">
        <p>
          Screening is powered by <strong className="text-ink">OpenSanctions</strong>,
          a purpose-built consolidated data provider. We do not rely on manual
          website lookups.
        </p>
        <div className="overline !text-sub mt-6 mb-3">Lists we screen against</div>
        <ul className="space-y-2">
          <Bullet testid="list-ofac">U.S. Treasury OFAC SDN and consolidated non-SDN lists</Bullet>
          <Bullet testid="list-eu">European Union consolidated financial-sanctions list</Bullet>
          <Bullet testid="list-un">United Nations Security Council consolidated list</Bullet>
          <Bullet testid="list-uk">United Kingdom OFSI consolidated list</Bullet>
          <Bullet testid="list-pep">Global PEP (Politically Exposed Person) databases</Bullet>
        </ul>
        <div className="overline !text-sub mt-8 mb-3">When we screen</div>
        <p>
          At intake (the corporate profile a buyer or supplier submits before
          approval), and again before any deal advances past the initial probe
          stage. Both the legal entity and every declared beneficial owner are
          screened.
        </p>
        <div className="overline !text-sub mt-8 mb-3">Adverse-hit policy</div>
        <p>
          A confirmed match on any list terminates the deal and freezes the
          account. Screening results are audit-logged with timestamp and
          reviewing analyst.
        </p>
      </Section>

      <Section eyebrow="03 · KYC data we collect" title="The corporate profile every counterparty submits." testid="trust-kyc">
        <p>
          Before any deal can be initiated, buyers and suppliers complete a
          corporate KYC profile capturing:
        </p>
        <ul className="mt-4 space-y-2">
          <Bullet>Legal entity name, jurisdiction of incorporation, and registration number</Bullet>
          <Bullet>All beneficial owners with 25% or greater ownership</Bullet>
          <Bullet>Countries of operation</Bullet>
          <Bullet>Politically-Exposed-Person disclosure</Bullet>
          <Bullet>Three signed attestations: no sanctioned exposure, lawful source of funds, and accuracy of beneficial-ownership declaration</Bullet>
        </ul>
        <p className="mt-4 text-sm">
          Nothing collected is stored beyond what is necessary for compliance
          review. The full policy governing data handling is available on
          request.
        </p>
      </Section>

      <Section eyebrow="04 · Deal-level controls" title="How the compliance gate is enforced in the platform itself." testid="trust-controls">
        <ul className="space-y-2">
          <Bullet>Buyer identity is stripped from every request before it is presented to any supplier — this is a technical control, not just a policy statement.</Bullet>
          <Bullet>Deal documents are issued in a strict sequence: NCNDA is executed first, then IMFPA is lodged with the transaction banks, and only then is the SPA released.</Bullet>
          <Bullet>Every generated document (NCNDA, IMFPA, SPA) is audit-logged with timestamp and the admin who generated it.</Bullet>
          <Bullet>Buyers with unresolved KYC status cannot submit an ICPO. Enforcement is in the API, not just the user interface.</Bullet>
        </ul>
      </Section>

      <Section eyebrow="05 · Compliance inquiries" title="Contact the desk." testid="trust-contact">
        <p>
          Compliance officers, counsel and correspondent banks are welcome to
          contact the desk directly:
        </p>
        <div className="mt-6 border border-line bg-parchment2 p-6">
          <div className="overline">Compliance contact</div>
          <a href={`mailto:${complianceEmail}`}
             className="h-serif text-2xl text-ink hover:text-oxblood transition-colors mt-2 inline-block"
             data-testid="trust-email">
            {complianceEmail}
          </a>
          <p className="text-sub text-sm mt-4">
            Response within two business days.
          </p>
        </div>
      </Section>

      <section className="max-w-4xl mx-auto px-6 md:px-10 py-14 border-t border-line" data-testid="trust-footer-meta">
        <div className="flex items-baseline justify-between text-xs font-mono text-sub uppercase tracking-wider">
          <span>Last reviewed: {lastReviewed}</span>
          <Link to="/process" className="link">Read the deal-flow pipeline →</Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
